import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Sparkles, X, ChevronDown, ChevronUp, Table } from 'lucide-react';
import * as XLSX from 'xlsx';
import { pedirTextoIA } from '../../utils/aiClient';

// Normaliza el nombre de la compañía a su forma canónica (las que usa NEBB).
// Las compañías a veces vienen con razón social completa en el PDF
// (ej. "BERKLEY INTERNATIONAL SEGUROS S.A. (URUGUAY)" → "Berkley").
const COMPANIAS_CANONICAS = [
  'Barbuss', 'Berkley', 'BSE', 'Cardif', 'Equinoccial', 'HDI', 'Integrity',
  'Mapfre', 'Metlife', 'Patria', 'Porto Seguro', 'Royal & Sun Alliance',
  'Sancor', 'San Cristóbal', 'SBI', 'Surco', 'Sura',
];
// Rescata todos los objetos JSON { ... } completos y bien balanceados de un
// texto, ignorando lo que venga cortado al final (respuesta truncada por
// límite de tokens). Respeta llaves dentro de strings y caracteres escapados.
function recoverObjects(text) {
  const out = [];
  if (!text) return out;
  let depth = 0, start = -1, inStr = false, esc = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inStr) {
      if (esc) esc = false;
      else if (ch === '\\') esc = true;
      else if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') { inStr = true; continue; }
    if (ch === '{') { if (depth === 0) start = i; depth++; }
    else if (ch === '}') {
      depth--;
      if (depth === 0 && start !== -1) {
        try { out.push(JSON.parse(text.slice(start, i + 1))); } catch { /* descartar fragmento inválido */ }
        start = -1;
      }
    }
  }
  return out;
}

export function normalizeCompany(raw) {
  if (!raw) return '';
  const s = raw.toString().toLowerCase();
  // Coincidencias por palabra clave (orden importa: primero las más específicas)
  if (s.includes('berkley')) return 'Berkley';
  if (s.includes('porto')) return 'Porto Seguro';
  if (s.includes('san cris') || s.includes('san crist')) return 'San Cristóbal';
  if (s.includes('royal') || s.includes('sun alliance') || s.includes('rsa')) return 'Royal & Sun Alliance';
  if (s.includes('sura') || s.includes('seguros sura')) return 'Sura';
  if (s.includes('mapfre')) return 'Mapfre';
  if (s.includes('sancor')) return 'Sancor';
  if (s.includes('metlife') || s.includes('met life')) return 'Metlife';
  if (s.includes('equinoc')) return 'Equinoccial';
  if (s.includes('cardif')) return 'Cardif';
  if (s.includes('integrity')) return 'Integrity';
  if (s.includes('patria')) return 'Patria';
  if (s.includes('surco')) return 'Surco';
  if (s.includes('barbuss')) return 'Barbuss';
  if (s.includes('hdi')) return 'HDI';
  if (s.includes('sbi')) return 'SBI';
  if (s.includes('bse') || s.includes('banco de seguros')) return 'BSE';
  // La corredora propia NO es una compañía aseguradora: si el extractor agarró
  // el nombre de NEBB por error, lo descartamos para que se pida carga manual.
  if (s.includes('nebb')) return '';
  // Si no matchea, devolver el texto original con formato Título (sin S.A., etc.)
  const limpio = raw.replace(/\b(s\.?a\.?|s\.?r\.?l\.?|seguros|international|uruguay|\(.*\))\b/gi, '').trim();
  return limpio || '';
}

// Lista de compañías conocidas para el selector de carga manual.
export const COMPANIAS_CONOCIDAS = ['Berkley', 'Porto Seguro', 'San Cristóbal', 'Royal & Sun Alliance', 'Sura', 'Mapfre', 'Sancor', 'Metlife', 'Equinoccial', 'Cardif', 'Integrity', 'Patria', 'Surco', 'Barbuss', 'HDI', 'SBI', 'BSE'];

// ¿La compañía extraída es inválida? (vacía, o quedó como la corredora propia)
export function companiaInvalida(c) {
  const v = (c || '').toString().trim().toLowerCase();
  return !v || v.includes('nebb') || v === 'sin compañía' || v === 'sin compania';
}

// ¿El nombre de cliente extraído es en realidad la corredora propia (NEBB)?
// En muchas pólizas el corredor NEBB figura muy destacado ("DATOS DEL CORREDOR
// DE SEGUROS: NEBB S.R.L.") y el extractor a veces lo toma como asegurado por
// error. El cliente real es el ASEGURADO (ej. "EDIFICIO PARAGUAY"), nunca NEBB.
// Detectamos esto para vaciar el nombre y forzar revisión manual.
export function clienteEsBroker(nombre) {
  const v = (nombre || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  if (!v) return false;
  // "nebb", "nebbrokers", "nebb s.r.l.", "nebb srl", etc.
  return /\bnebb\b/.test(v) || v.includes('nebbrokers');
}

// Columnas soportadas: Nombre, Razón Social (RUT), Email, Teléfono, Teléfono Particular
// + opcionales: N° Póliza, Compañía, Cobertura, Prima, Matrícula, etc.
export function ExcelUpload({ onDataExtracted, onClose, existingClients = [], existingPolicies = [] }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const normalizeHeader = (h) => {
    // Normalizar: minúsculas, sin acentos, sin espacios extra
    const s = String(h).toLowerCase().trim()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Nombre del cliente
    if (s === 'nombre' || s === 'nombre cliente' || s === 'cliente') return 'clientName';
    // Razón Social → va al campo RUT/DNI (identificación de empresa)
    if (s === 'razon social' || s === 'razonsocial' || s === 'razon' ||
        s === 'rut' || s === 'dni' || s === 'ci' || s === 'cedula' ||
        s === 'cuit' || s === 'identificacion' || s === 'nro doc') return 'clientDni';
    // Email
    if (s.includes('email') || s.includes('correo') || s === 'mail') return 'clientEmail';
    // Teléfono particular → secundario
    if (s.includes('particular')) return 'clientPhone2';
    // Teléfono principal
    if (s === 'telefono' || s === 'tel' || s === 'celular' || s === 'cel' ||
        s === 'movil' || s === 'phone' || s === 'whatsapp') return 'clientPhone';
    // Dirección
    if (s.includes('direcci') || s === 'address') return 'clientAddress';
    // Póliza
    if (s.includes('poliza') || s === 'nro poliza' || s === 'numero poliza' || s === 'n poliza') return 'policyNumber';
    // Compañía
    if (s.includes('compa') || s.includes('aseguradora') || s === 'empresa') return 'company';
    // Ramo / Tipo
    if (s === 'tipo' || s === 'ramo' || s === 'type') return 'type';
    // Cobertura
    if (s.includes('cobertura')) return 'coverage';
    // Prima
    if (s === 'prima' || s === 'monto' || s === 'premio' || s === 'importe') return 'premium';
    // Fechas
    if (s.includes('inicio') || s.includes('desde') || s === 'start') return 'startDate';
    if (s.includes('vencim') || s.includes('hasta') || s === 'fin') return 'endDate';
    // Vehículo
    if (s === 'marca') return 'assetBrand';
    if (s === 'modelo') return 'assetModel';
    if (s.includes('matricula') || s.includes('patente') || s === 'placa') return 'assetPlate';
    return null;
  };

  const isDupePolicy = (r) => r.policyNumber && existingPolicies.some(
    p => p.number?.trim().toLowerCase() === r.policyNumber?.trim().toLowerCase()
  );

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx','xls','csv'].includes(ext)) { setError('Solo .xlsx, .xls o .csv'); return; }
    setFile(f); setError(''); setLoading(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'binary', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (raw.length < 2) { setError('El archivo está vacío'); setLoading(false); return; }
        const headers = raw[0].map(h => normalizeHeader(h));
        const rows = raw.slice(1)
          .filter(r => r.some(c => String(c).trim() !== ''))
          .map(row => {
            const obj = {};
            headers.forEach((key, i) => { if (key) obj[key] = String(row[i] || '').trim(); });
            // Teléfono particular: si no hay teléfono principal, usarlo; si hay ambos, guardar en notas
            if (obj.clientPhone2) {
              if (!obj.clientPhone) {
                obj.clientPhone = obj.clientPhone2;
              } else {
                obj.clientNotes = `Tel. particular: ${obj.clientPhone2}`;
              }
              delete obj.clientPhone2;
            }
            return obj;
          })
          .filter(r => r.clientName);
        if (rows.length === 0) {
          setError('No se encontraron filas con nombre. Verificá que la primera fila tenga encabezados: Nombre, Razón Social, Email, Teléfono, etc.');
          setLoading(false); return;
        }
        setPreview(rows);
        const sel = {};
        rows.forEach((r, i) => { sel[i] = !isDupePolicy(r); });
        setSelected(sel);
      } catch (err) { setError('Error al leer el archivo: ' + err.message); }
      setLoading(false);
    };
    reader.onerror = () => { setError('No se pudo leer el archivo. Probá de nuevo.'); setLoading(false); };
    reader.readAsBinaryString(f);
  };

  const toggleSelect = (i) => setSelected(s => ({ ...s, [i]: !s[i] }));
  const toggleAll = () => {
    const allOn = preview.every((_, i) => selected[i]);
    const next = {};
    preview.forEach((_, i) => { next[i] = !allOn; });
    setSelected(next);
  };
  const handleConfirm = () => { onDataExtracted(preview.filter((_, i) => selected[i])); onClose(); };
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 780 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Table size={18} color="var(--accent)" />
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>Importar desde Excel / CSV</h2>
          </div>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
            <strong style={{ color: 'var(--text-primary)' }}>Columnas reconocidas:</strong> Nombre · Razón Social (→ RUT) · Email · Teléfono · Teléfono Particular · y opcionales: N° Póliza, Compañía, Prima, Matrícula, etc.
          </div>
          {!preview ? (
            <>
              <div onClick={() => document.getElementById('excel-input-v2').click()} style={{
                border: `2px dashed ${file ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 12, padding: 32, textAlign: 'center', cursor: 'pointer',
                background: file ? 'var(--accent-dim)' : 'var(--bg-input)',
              }}>
                <input id="excel-input-v2" type="file" onClick={e => { e.target.value = null; }} accept=".xlsx,.xls,.csv" onChange={handleFile} style={{ display: 'none' }} />
                {loading ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Leyendo archivo...</div>
                  : file ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <Table size={28} color="var(--accent)" /><span style={{ fontWeight: 600, fontSize: 13 }}>{file.name}</span>
                    </div>
                  : <div style={{ color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <Table size={28} /><span style={{ fontSize: 13 }}>Clic para seleccionar Excel o CSV</span>
                      <span style={{ fontSize: 11 }}>La primera fila debe tener los encabezados de columna</span>
                    </div>
                }
              </div>
              {error && <div style={{ background: 'var(--red-dim)', border: '1px solid rgba(255,77,109,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)' }}>{error}</div>}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle size={15} color="var(--accent)" />
                  <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--accent)' }}>
                    {preview.length} registro{preview.length !== 1 ? 's' : ''} encontrado{preview.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={toggleAll}>
                  {preview.every((_, i) => selected[i]) ? 'Deseleccionar todo' : 'Seleccionar todo'}
                </button>
              </div>
              <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-secondary)', position: 'sticky', top: 0 }}>
                      <th style={{ width: 36, padding: '8px 12px' }}></th>
                      {['Nombre','RUT/Razón Social','Teléfono','Email','N° Póliza','Estado'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r, i) => {
                      const dupe = isDupePolicy(r);
                      const clientOnly = !r.policyNumber && !r.company;
                      return (
                        <tr key={i} style={{ borderTop: '1px solid var(--border)', background: selected[i] ? 'rgba(124,108,248,0.03)' : 'transparent', opacity: dupe ? 0.5 : 1, cursor: dupe ? 'not-allowed' : 'pointer' }}
                          onClick={() => !dupe && toggleSelect(i)}>
                          <td style={{ padding: '8px 12px' }}>
                            <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${selected[i] && !dupe ? 'var(--accent)' : 'var(--border)'}`, background: selected[i] && !dupe ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {selected[i] && !dupe && <CheckCircle size={10} color="#fff" />}
                            </div>
                          </td>
                          <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 500 }}>
                            {r.clientName}
                            {dupe && <span className="badge badge-red" style={{ fontSize: 9, marginLeft: 6 }}>Póliza dup.</span>}
                            {clientOnly && <span className="badge badge-blue" style={{ fontSize: 9, marginLeft: 6 }}>Solo cliente</span>}
                          </td>
                          <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>{r.clientDni || '—'}</td>
                          <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>{r.clientPhone || '—'}</td>
                          <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>{r.clientEmail || '—'}</td>
                          <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace' }}>{r.policyNumber || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>sin póliza</span>}</td>
                          <td style={{ padding: '8px 12px', fontSize: 12 }}>
                            {dupe ? <span style={{ color: 'var(--red)' }}>Duplicado</span>
                              : clientOnly ? <span style={{ color: '#60a5fa' }}>Solo cliente</span>
                              : <span style={{ color: 'var(--accent)' }}>Con póliza</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }}
                onClick={() => { setPreview(null); setFile(null); setSelected({}); }}>
                ← Cargar otro archivo
              </button>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
          {preview && (
            <button onClick={handleConfirm} className="btn btn-primary" disabled={selectedCount === 0}>
              <CheckCircle size={14} /> Importar {selectedCount} registro{selectedCount !== 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// La API key va en server.js, no acá (evita el error CORS)

export default function PdfUpload({ onDataExtracted, onClose }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);      // array de pólizas encontradas
  const [selected, setSelected] = useState({});      // { index: true/false }
  const [expanded, setExpanded] = useState({});      // { index: true/false }

  const handleFile = (e) => {
    const fs = Array.from(e.target.files || []).filter(f => f.type === 'application/pdf');
    if (fs.length) { setFile(fs); setError(''); setResults(null); setSelected({}); }
    else setError('Por favor seleccioná archivos PDF.');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const fs = Array.from(e.dataTransfer.files || []).filter(f => f.type === 'application/pdf');
    if (fs.length) { setFile(fs); setError(''); setResults(null); setSelected({}); }
  };

  const extractData = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const allParsed = [];
      const fallos = []; // { nombre, motivo } por cada PDF que no se pudo procesar

      // Procesa UN PDF y devuelve el array de pólizas extraídas (o lanza error).
      // Se lee/recorta el PDF y se llama a la IA. Al estar en una función, las
      // 9 cargas pueden lanzarse EN PARALELO (mucho más rápido que en serie).
      const procesarPDF = async (f) => {
      // Leer como bytes para poder limpiar envoltorios (algunos sitios entregan
      // el PDF envuelto en multipart/form-data en vez del PDF puro).
      const buf = await f.arrayBuffer();
      let bytes = new Uint8Array(buf);

      // Buscar la firma "%PDF" (0x25 0x50 0x44 0x46). Si no está al inicio,
      // el archivo trae un envoltorio: recortamos desde el %PDF real.
      const sig = [0x25, 0x50, 0x44, 0x46];
      let start = -1;
      for (let i = 0; i < bytes.length - 4; i++) {
        if (bytes[i] === sig[0] && bytes[i+1] === sig[1] && bytes[i+2] === sig[2] && bytes[i+3] === sig[3]) { start = i; break; }
      }
      if (start === -1) {
        throw new Error('El archivo no es un PDF válido. Si lo descargaste de la web de la compañía, probá abrirlo y volver a guardarlo como PDF (en Vista Previa: Archivo → Exportar como PDF).');
      }
      if (start > 0) {
        // Recortar el envoltorio inicial
        bytes = bytes.slice(start);
        // Recortar lo que sobre después del último %%EOF (cierre del PDF)
        const eofSig = [0x25, 0x25, 0x45, 0x4F, 0x46]; // %%EOF
        let end = -1;
        for (let i = bytes.length - 5; i >= 0; i--) {
          if (bytes[i]===eofSig[0] && bytes[i+1]===eofSig[1] && bytes[i+2]===eofSig[2] && bytes[i+3]===eofSig[3] && bytes[i+4]===eofSig[4]) { end = i + 5; break; }
        }
        if (end > 0 && end < bytes.length) bytes = bytes.slice(0, end);
      }

      // Convertir bytes a base64 (por chunks para no reventar el call stack)
      let binary = '';
      const chunk = 8192;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
      }
      const base64 = btoa(binary);

      const { texto: text, truncado } = await pedirTextoIA({
        model: 'claude-haiku-4-5-20251001',
        maxTokens: 16000,
        permitirTruncado: true, // si se corta, igual intentamos recuperar objetos
        content: [
              {
                type: 'document',
                source: { type: 'base64', media_type: 'application/pdf', data: base64 }
              },
              {
                type: 'text',
                text: `Analizá este documento que puede contener UNA O VARIAS pólizas de seguros o avisos de renovación.
Identificá cada póliza/cliente por separado y devolvé un array JSON.
Devolvé ÚNICAMENTE el JSON, sin markdown ni texto adicional.

Formato exacto (array, aunque sea una sola póliza):
[
  {
    "clientName": "",
    "clientDni": "",
    "clientPhone": "",
    "clientEmail": "",
    "clientAddress": "",
    "policyNumber": "",
    "company": "",
    "type": "auto|home|commercial|life|other",
    "coverage": "",
    "premium": 0,
    "startDate": "YYYY-MM-DD",
    "endDate": "YYYY-MM-DD",
    "isRenewalNotice": false,
    "renewalMonth": "",
    "noticeDate": "YYYY-MM-DD",
    "renewalDateLogic": "future|prev_end|unknown",
    "assetBrand": "",
    "assetModel": "",
    "assetYear": "",
    "assetPlate": "",
    "assetVin": "",
    "assetEngine": "",
    "assetPadron": "",
    "assetCategory": "",
    "assetUse": "",
    "assetZone": "",
    "assetFuel": "",
    "assetColor": "",
    "assetAddress": "",
    "assetType": "",
    "assetArea": "",
    "assetValue": "",
    "deducible": "",
    "cuotas": "",
    "notes": ""
  }
]
Si un campo no existe en el documento, dejalo como cadena vacía o 0.
Si hay múltiples pólizas, cada una va como un objeto separado en el array.

DATOS DEL VEHÍCULO — extraé TODO lo que encuentres, preferimos datos de más:
- assetEngine: número de MOTOR (importante).
- assetPadron: número de PADRÓN/empadronamiento.
- assetVin: número de CHASIS / VIN.
- assetCategory: categoría (ej. "Automóviles").
- assetUse: destino/uso (ej. "Particular").
- assetZone: zona de circulación (ej. "Montevideo").
- assetFuel / assetColor: combustible y color si figuran.
Cualquier otro dato del vehículo que no tenga campo propio, agregalo en "notes".

AVISO DE RENOVACIÓN — detección, fechas y lógica por compañía:
- isRenewalNotice: true si el documento es un AVISO DE RENOVACIÓN o AVISO DE VENCIMIENTO (suele decir "AVISO DE RENOVACIÓN", "RENOVACIÓN" o "AVISO DE VENCIMIENTO" en el encabezado), false si es una póliza/certificado normal.

- noticeDate: la FECHA CLAVE que marca el aviso, en formato YYYY-MM-DD. Según la compañía es una u otra cosa (ver tabla abajo). Copiala EXACTA del documento, sin inventar. Si no la encontrás, dejala vacía.

- renewalDateLogic: indica CÓMO interpretar las fechas. Hay dos casos según la compañía:
  * "future"   → El aviso YA trae la vigencia NUEVA (futura, el período renovado). En este caso poné startDate/endDate con esas fechas nuevas EXACTAS del documento, y noticeDate = el inicio de esa vigencia nueva.
  * "prev_end" → El aviso marca una "fecha de renovación" o "vencimiento" que es el FIN de la vigencia PASADA (la que está por vencer). En este caso poné noticeDate = esa fecha de vencimiento/renovación, y dejá startDate/endDate vacíos (el sistema calcula la vigencia nueva = noticeDate + 1 año).

TABLA POR COMPAÑÍA (usala para decidir renewalDateLogic y de dónde sacar noticeDate):
- SBI (automóviles y hogar): "prev_end". El campo "Fecha renovacion" es el fin de la vigencia pasada → noticeDate = esa fecha.
- MAPFRE (automóviles): "prev_end". El campo "VENCIMIENTO" es el fin de la vigencia pasada → noticeDate = ese vencimiento.
- BSE (aviso de vehículos, combinados, ramos varios): "prev_end". El campo "Vencimiento" es el fin de la vigencia pasada → noticeDate = ese vencimiento. (NO uses "Fecha Cotización".)
- BARBUSS (aviso de VENCIMIENTO, combinado/hogar): "prev_end". El "Vencimiento" / fin de vigencia es el fin de la vigencia pasada → noticeDate = esa fecha.
- SURA (renovación automóviles): "future". El aviso da "Vigencia desde DD/MM/AAAA hasta DD/MM/AAAA" que es la NUEVA → startDate/endDate = esas fechas exactas; noticeDate = el "desde".
- PORTO SEGURO: "future". El aviso da "Vigencia DD/MM/AAAA - DD/MM/AAAA". OJO: a veces muestra la vigencia ANTERIOR (la que vence). La renovación arranca en la fecha que da el aviso como inicio del próximo período. Si solo figura un período (el que vence), tratalo como noticeDate y poné renewalDateLogic "prev_end". Si figura claramente el período nuevo (futuro), usalo como startDate/endDate con logic "future".
- SAN CRISTOBAL: "future". "Datos de la Póliza Nueva / Vigencia: Desde DD/MM/AAAA hasta DD/MM/AAAA" → startDate/endDate = esas fechas; noticeDate = el "Desde".
- BARBUSS (aviso de RENOVACIÓN automóviles): "future". "Vigencia: Un año, desde DD/MM/AAAA hasta DD/MM/AAAA" (vigencia nueva) → startDate/endDate = esas; noticeDate = el "desde".
- Cualquier otra compañía: si el aviso muestra claramente una vigencia futura (posterior a hoy), usá "future"; si solo marca una fecha de vencimiento de lo que está por vencer, usá "prev_end". Si no estás seguro, "unknown".

REGLAS CRÍTICAS sobre TODAS las fechas:
1. Copiá las fechas EXACTAS y TEXTUALES del documento. No las deduzcas ni inventes. NUNCA pongas una vigencia "redonda" por defecto (como 1 de julio a 30 de junio) si no figura así en el documento.
2. Las fechas en documentos uruguayos/brasileños vienen en formato DD/MM/AAAA: el primer número es el DÍA, el segundo el MES. Convertí a YYYY-MM-DD sin invertir día y mes.
3. El año debe ser el que figura escrito. Verificá dos veces el año: un error de año invalida la extracción.
4. VIGENCIA de la póliza (startDate / endDate): buscá la línea de vigencia del contrato y extraé sus DOS fechas.
   - El texto suele decir: "Vigencia desde el: DD/MM/AAAA hasta las 24hs del: DD/MM/AAAA" (Porto Seguro y similares). La PRIMERA fecha es startDate, la SEGUNDA (la que va después de "hasta las 24hs del:" o "hasta el:" o "hasta:") es endDate. El texto "las 24hs del" es solo una aclaración horaria, IGNORALO: la fecha que le sigue es el vencimiento.
   - Otras variantes: "Vigencia: DD/MM/AAAA - DD/MM/AAAA", "Desde DD/MM/AAAA hasta DD/MM/AAAA". Siempre la primera es inicio y la segunda es fin.
   - La vigencia NO necesariamente dura un año exacto. Los ENDOSOS suelen tener vigencia parcial (ej. desde hoy hasta el fin de la póliza original, pocos meses). Copiá las fechas tal cual figuran, aunque el período sea menor a un año.
   - Si el documento solo trae UNA fecha de vigencia clara, poné esa en startDate y dejá endDate vacío. No inventes el fin.
5. renewalMonth: dejalo por compatibilidad. Si es aviso de renovación, poné el MES (dos dígitos "MM") de noticeDate. Si no hay noticeDate, vacío.

REGLAS CRÍTICAS sobre los DATOS DEL CLIENTE:
1. El cliente es el ASEGURADO (o TOMADOR). Ignorá por completo los datos del CORREDOR ASESOR (NEBB, NEBB S.R.L., NEBBROKERS, matrícula/número de corredor): NUNCA son el cliente. El nombre del cliente JAMÁS puede ser "NEBB", "NEBB S.R.L." ni similar — si lo único que ves cerca es eso, dejá clientName VACÍO.
2. En las pólizas del BANCO DE SEGUROS DEL ESTADO (BSE) el asegurado figura arriba en el campo "Asegurado:" (ej. "EDIFICIO PARAGUAY") y la corredora NEBB figura más abajo en un recuadro "DATOS DEL CORREDOR DE SEGUROS". El cliente es el del campo "Asegurado:", NO el del recuadro del corredor.
3. clientEmail: SOLO si es claramente el email personal del asegurado. Si el único email del documento es el domicilio electrónico constituido en la casilla del corredor (ej. termina en @nebbrokers.com o aparece junto a "domicilio electrónico" o "Corredor"), dejá clientEmail VACÍO.
4. clientPhone: el teléfono que figura junto al nombre/dirección del asegurado, nunca el del corredor (ej. 29155202 es el del corredor → no usar).
5. premium: si el documento es un AVISO con tabla de precios por cobertura (Cob. 1, Cob. 2...), premium es el valor CONTADO de la fila de la cobertura ACTUAL (la que el documento indica como "Cobertura actual"). NUNCA uses un valor de cuotas (2, 3, 5, 6 o 10 cuotas) como premium.`
              }
            ],
      });

      let parsed;
      try {
        let clean = text.replace(/```json|```/g, '').trim();
        const arrMatch = clean.match(/\[[\s\S]*\]/);
        const objMatch = clean.match(/\{[\s\S]*\}/);
        if (arrMatch) parsed = JSON.parse(arrMatch[0]);
        else if (objMatch) parsed = JSON.parse(objMatch[0]);
        else parsed = JSON.parse(clean);
        if (!Array.isArray(parsed)) parsed = [parsed];
      } catch {
        // Recuperación: si el JSON vino truncado (respuesta cortada a mitad),
        // rescatamos todos los objetos { ... } completos y bien balanceados que
        // haya, en vez de perder el archivo entero.
        parsed = recoverObjects(text);
        if (!parsed.length) {
          throw new Error(truncado
            ? 'La respuesta de la IA se cortó por tamaño (PDF con muchas pólizas). Cargá ese PDF solo, o dividilo.'
            : 'No se pudo interpretar la respuesta de la IA (formato inusual).');
        }
      }

      // Normalizar el nombre de la compañía a su forma canónica
      parsed = parsed.map(r => r && r.company ? { ...r, company: normalizeCompany(r.company) } : r);
      // Si el extractor tomó la corredora (NEBB) como cliente, vaciar ese nombre
      // para que la fila quede marcada y se complete con el asegurado real.
      parsed = parsed.map(r => (r && clienteEsBroker(r.clientName)) ? { ...r, clientName: '' } : r);
      return parsed;
      }; // fin de procesarPDF

      // Lanzar todos los PDFs EN PARALELO. allSettled espera a que terminen
      // todos sin que uno que falla cancele a los demás.
      const resultados = await Promise.allSettled(file.map(f => procesarPDF(f)));
      resultados.forEach((res, i) => {
        if (res.status === 'fulfilled') {
          allParsed.push(...res.value);
        } else {
          fallos.push({ nombre: file[i].name, motivo: res.reason?.message || 'error desconocido' });
          console.error('Error procesando PDF', file[i].name, res.reason);
        }
      });

      if (allParsed.length === 0) {
        // Ningún PDF dio resultado: mostramos el detalle de los fallos.
        const detalle = fallos.map(x => `• ${x.nombre}: ${x.motivo}`).join('\n');
        throw new Error('No se pudo extraer ninguna póliza.\n' + detalle);
      }

      setResults(allParsed);
      // Seleccionar todas por defecto y expandir la primera
      const sel = {};
      allParsed.forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
      setExpanded({ 0: true });
      // Si algunos PDFs fallaron pero otros sí salieron, avisamos sin bloquear.
      if (fallos.length > 0) {
        setError(`Se procesaron ${allParsed.length} póliza(s). ${fallos.length} PDF(s) no se pudieron leer: ` +
          fallos.map(x => x.nombre).join(', ') + '. Probá cargarlos por separado.');
      }

    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (i) => setSelected(s => ({ ...s, [i]: !s[i] }));
  const toggleExpand = (i) => setExpanded(s => ({ ...s, [i]: !s[i] }));
  const toggleAll = () => {
    const allOn = results.every((_, i) => selected[i]);
    const next = {};
    results.forEach((_, i) => { next[i] = !allOn; });
    setSelected(next);
  };
  // Editar la compañía de una fila (cuando el extractor no la detectó)
  const setCompany = (i, value) => {
    setResults(rs => rs.map((r, idx) => idx === i ? { ...r, company: value } : r));
  };
  const setClientName = (i, value) => {
    setResults(rs => rs.map((r, idx) => idx === i ? { ...r, clientName: value } : r));
  };
  // Filas seleccionadas que tienen póliza pero les falta compañía válida.
  const faltanCompania = results
    ? results.map((r, i) => ({ r, i })).filter(({ r, i }) => selected[i] && (r.policyNumber || r.company !== undefined) && r.policyNumber && companiaInvalida(r.company))
    : [];
  // Filas seleccionadas a las que les falta el nombre del cliente (asegurado).
  // Pasa cuando el extractor tomó la corredora por error y lo vaciamos.
  const faltanCliente = results
    ? results.map((r, i) => ({ r, i })).filter(({ r, i }) => selected[i] && !((r.clientName || '').toString().trim()))
    : [];

  const handleConfirm = () => {
    if (!results) return;
    // No permitir cargar pólizas sin compañía: hay que completarla primero.
    if (faltanCompania.length > 0) {
      setError(`${faltanCompania.length} póliza(s) seleccionada(s) no tienen compañía. Expandí cada una y seleccioná la compañía antes de cargar.`);
      // Abrir la primera que falta para que el usuario la vea
      setExpanded(s => ({ ...s, [faltanCompania[0].i]: true }));
      return;
    }
    // No permitir cargar sin cliente (asegurado): evita que quede asignada a NEBB.
    if (faltanCliente.length > 0) {
      setError(`${faltanCliente.length} póliza(s) seleccionada(s) no tienen cliente. Expandí cada una y escribí el nombre del ASEGURADO (no la corredora) antes de cargar.`);
      setExpanded(s => ({ ...s, [faltanCliente[0].i]: true }));
      return;
    }
    const arr = Array.isArray(results) ? results : [results];
    const toLoad = arr.filter((_, i) => selected[i]);
    onDataExtracted(toLoad); // lote completo en una llamada: permite el resumen de carga
    onClose();
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const fieldLabel = (r) => {
    if (r.type === 'auto') return [r.assetBrand, r.assetModel, r.assetPlate].filter(Boolean).join(' ');
    if (r.type === 'home') return r.assetAddress || r.assetType || '';
    return r.coverage || '';
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sparkles size={18} color="var(--accent)" />
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>Carga automática con IA</h2>
          </div>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>

        <div className="modal-body">
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Subí un PDF con una o varias pólizas/avisos de renovación. La IA detecta todas automáticamente.
          </p>

          {/* Drop zone */}
          {!results && (
            <div
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => document.getElementById('pdf-input').click()}
              style={{
                border: `2px dashed ${file ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 12, padding: 32, textAlign: 'center', cursor: 'pointer',
                background: file ? 'var(--accent-dim)' : 'var(--bg-input)',
                transition: 'all 0.2s',
              }}
            >
              <input id="pdf-input" type="file" onClick={e => { e.target.value = null; }} accept=".pdf" multiple onChange={handleFile} style={{ display: 'none' }} />
              {file ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <FileText size={32} color="var(--accent)" />
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{file.length === 1 ? file[0].name : `${file.length} PDFs seleccionados`}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{(file.reduce((s, f) => s + f.size, 0) / 1024).toFixed(0)} KB</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
                  <Upload size={32} />
                  <span style={{ fontSize: 13 }}>Arrastrá uno o varios PDFs acá o hacé clic para seleccionar</span>
                  <span style={{ fontSize: 11 }}>Soporta PDFs con múltiples pólizas o avisos de renovación</span>
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{ background: 'var(--red-dim)', border: '1px solid rgba(255,77,109,0.2)', borderRadius: 8, padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--red)' }}>
              <AlertCircle size={14} />{error}
            </div>
          )}

          {/* Results list */}
          {results && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle size={15} color="var(--accent)" />
                  <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--accent)' }}>
                    {results.length} póliza{results.length !== 1 ? 's' : ''} encontrada{results.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={toggleAll}>
                  {results.every((_, i) => selected[i]) ? 'Deseleccionar todo' : 'Seleccionar todo'}
                </button>
              </div>

              {/* Cards */}
              {results.map((r, i) => (
                <div key={i} style={{
                  background: 'var(--bg-input)', borderRadius: 10,
                  border: `1px solid ${selected[i] ? 'rgba(124,108,248,0.3)' : 'var(--border)'}`,
                  overflow: 'hidden', transition: 'border-color 0.2s',
                }}>
                  {/* Row header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', cursor: 'pointer' }}
                    onClick={() => toggleExpand(i)}>
                    {/* Checkbox */}
                    <div
                      onClick={e => { e.stopPropagation(); toggleSelect(i); }}
                      style={{
                        width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                        border: `2px solid ${selected[i] ? 'var(--accent)' : 'var(--border)'}`,
                        background: selected[i] ? 'var(--accent)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}
                    >
                      {selected[i] && <CheckCircle size={11} color="#fff" />}
                    </div>

                    {/* Summary */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'var(--font-display)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: !((r.clientName || '').toString().trim()) ? 'var(--red)' : undefined }}>
                        {r.clientName || '⚠ Falta cliente (asegurado)'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 10, marginTop: 2, flexWrap: 'wrap' }}>
                        {r.policyNumber && <span>{r.policyNumber}</span>}
                        {r.company && <span>· {r.company}</span>}
                        {fieldLabel(r) && <span>· {fieldLabel(r)}</span>}
                        {r.isRenewalNotice ? (
                          <span style={{ color: 'var(--accent)', fontWeight: 600 }}>· 🔄 Renovación{r.renewalMonth ? ` (mes ${r.renewalMonth})` : ''} → entra a renovar</span>
                        ) : (
                          <>
                            {r.endDate && <span>· vence {r.endDate}</span>}
                            {r.endDate && r.endDate < new Date().toISOString().split('T')[0] && (
                              <span style={{ color: '#f59e0b', fontWeight: 600 }}> ⚠ vigencia vencida — verificá el año</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Type badge */}
                    <span className="badge badge-gray" style={{ fontSize: 10, flexShrink: 0 }}>
                      {r.type === 'auto' ? '🚗 Auto' : r.type === 'home' ? '🏠 Inmueble' : '📄 ' + (r.type || 'otro')}
                    </span>

                    {/* Expand toggle */}
                    {expanded[i] ? <ChevronUp size={14} color="var(--text-muted)" /> : <ChevronDown size={14} color="var(--text-muted)" />}
                  </div>

                  {/* Expanded detail */}
                  {expanded[i] && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '12px 14px' }}>
                      {/* Compañía: editable. Si falta o es inválida, se pide cargarla. */}
                      {r.policyNumber && (
                        <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: companiaInvalida(r.company) ? 'var(--red-dim)' : 'var(--bg-input)', border: companiaInvalida(r.company) ? '1px solid rgba(248,113,113,0.3)' : '1px solid var(--border)' }}>
                          <div style={{ fontSize: 12, color: companiaInvalida(r.company) ? 'var(--red)' : 'var(--text-muted)', marginBottom: 6, fontWeight: 600 }}>
                            {companiaInvalida(r.company) ? '⚠ Compañía no detectada — seleccioná una para poder cargar' : 'Compañía'}
                          </div>
                          <select value={companiaInvalida(r.company) ? '' : r.company} onChange={e => setCompany(i, e.target.value)}
                            style={{ width: '100%', padding: '8px 10px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text-primary)', fontSize: 13 }}>
                            <option value="">— Seleccionar compañía —</option>
                            {COMPANIAS_CONOCIDAS.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      )}
                      {/* Cliente: editable. Si falta (o el extractor tomó NEBB),
                          se exige escribir el asegurado real antes de cargar. */}
                      {!((r.clientName || '').toString().trim()) && (
                        <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--red-dim)', border: '1px solid rgba(248,113,113,0.3)' }}>
                          <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 6, fontWeight: 600 }}>
                            ⚠ Cliente no detectado — escribí el ASEGURADO (no la corredora NEBB) para poder cargar
                          </div>
                          <input value={r.clientName || ''} onChange={e => setClientName(i, e.target.value)} placeholder="Nombre del asegurado"
                            style={{ width: '100%', padding: '8px 10px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text-primary)', fontSize: 13 }} />
                        </div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 20px', fontSize: 12 }}>
                      {[
                        ['Cliente', r.clientName], ['CI/RUT', r.clientDni],
                        ['Teléfono', r.clientPhone], ['Email', r.clientEmail],
                        ['Dirección', r.clientAddress],
                        ['N° Póliza', r.policyNumber],
                        ['Cobertura', r.coverage],
                        ['Prima', r.premium ? '$' + r.premium : ''],
                        ['Inicio', r.startDate], ['Vencimiento', r.endDate],
                        r.assetBrand && ['Vehículo', `${r.assetBrand} ${r.assetModel} ${r.assetYear}`.trim()],
                        r.assetPlate && ['Matrícula', r.assetPlate],
                        r.assetEngine && ['Motor', r.assetEngine],
                        r.assetPadron && ['Padrón', r.assetPadron],
                        r.assetVin && ['Chasis/VIN', r.assetVin],
                        r.assetAddress && ['Dirección bien', r.assetAddress],
                        r.assetType && ['Tipo inmueble', r.assetType],
                        r.notes && ['Notas', r.notes],
                      ].filter(Boolean).map(([k, v]) => v ? (
                        <div key={k} style={{ display: 'flex', gap: 6 }}>
                          <span style={{ color: 'var(--text-muted)', minWidth: 90, flexShrink: 0 }}>{k}:</span>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{String(v)}</span>
                        </div>
                      ) : null)}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Change file */}
              <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }}
                onClick={() => { setResults(null); setFile(null); setSelected({}); }}>
                ← Cargar otro PDF
              </button>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
          {!results ? (
            <button onClick={extractData} className="btn btn-primary" disabled={!file || loading}>
              {loading ? (
                <><span className="spinner" style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(0,0,0,0.2)', borderTopColor: '#fff', borderRadius: '50%' }} /> Analizando {file && file.length > 1 ? `${file.length} PDFs...` : '...'}</>
              ) : (
                <><Sparkles size={14} /> Extraer con IA</>
              )}
            </button>
          ) : (
            <button onClick={handleConfirm} className="btn btn-primary" disabled={selectedCount === 0 || faltanCompania.length > 0}>
              <CheckCircle size={14} /> {faltanCompania.length > 0 ? `Falta compañía en ${faltanCompania.length}` : `Cargar ${selectedCount} póliza${selectedCount !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}